import {useEffect, useEffectEvent, useState} from 'react'
import {Pressable, View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'
import {FocusGuards, FocusScope} from 'radix-ui/internal'

import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {Logo} from '#/view/icons/Logo'
import {Logotype} from '#/view/icons/Logotype'
import {atoms as a, flatten, useBreakpoints, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {type WelcomeModalControl} from '#/components/hooks/useWelcomeModal.shared'
import {TimesLarge_Stroke2_Corner0_Rounded as XIcon} from '#/components/icons/Times'
import {Text} from '#/components/Typography'
import {useAnalytics} from '#/analytics'

const LIGHT = '#F5F0FF'
const LIGHT_MUTED = '#C8BFD9'
const MAGENTA = '#DC34FE'

interface WelcomeModalProps {
  control: WelcomeModalControl
}

export function WelcomeModal({control}: WelcomeModalProps) {
  const {t: l} = useLingui()
  const ax = useAnalytics()
  const {requestSwitchToAccount} = useLoggedOutViewControls()
  const {gtMobile} = useBreakpoints()
  const [isExiting, setIsExiting] = useState(false)
  const [signInLinkHovered, setSignInLinkHovered] = useState(false)

  const fadeOutAndClose = (callback?: () => void) => {
    setIsExiting(true)
    setTimeout(() => {
      control.close()
      if (callback) callback()
    }, 150)
  }

  const onPresented = useEffectEvent(() => {
    ax.metric('welcomeModal:presented', {})
  })

  useEffect(() => {
    if (control.isOpen) {
      onPresented()
    }
  }, [control.isOpen])

  const onPressCreateAccount = () => {
    ax.metric('welcomeModal:signupClicked', {})
    control.close()
    requestSwitchToAccount({requestedAccount: 'new'})
  }

  const onPressExplore = () => {
    ax.metric('welcomeModal:exploreClicked', {})
    fadeOutAndClose()
  }

  const onPressSignIn = () => {
    ax.metric('welcomeModal:signinClicked', {})
    control.close()
    requestSwitchToAccount({requestedAccount: 'existing'})
  }

  FocusGuards.useFocusGuards()

  return (
    <View
      role="dialog"
      aria-modal
      style={[
        a.fixed,
        a.inset_0,
        a.justify_center,
        a.align_center,
        {zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.2)'},
        web({backdropFilter: 'blur(15px)'}),
        isExiting ? a.fade_out : a.fade_in,
      ]}>
      <FocusScope.FocusScope asChild loop trapped>
        <View
          style={flatten([
            {
              maxWidth: 800,
              maxHeight: 600,
              width: '90%',
              height: '90%',
              backgroundColor: '#07060f',
            },
            a.rounded_lg,
            a.overflow_hidden,
            a.zoom_in,
            a.relative,
          ])}>
          {/* web-only decorative backdrop */}
          <div className="welcome-cyber-grid" aria-hidden>
            <div className="welcome-cyber-grid__sky" />
            <div className="welcome-cyber-grid__horizon" />
            <div className="welcome-cyber-grid__floor" />
          </div>
          <View
            style={[
              a.flex_1,
              a.justify_center,
              a.gap_2xl,
              a.align_center,
              a.p_4xl,
              {zIndex: 1},
            ]}>
            <View
              style={[
                a.flex_row,
                a.align_center,
                a.justify_center,
                a.w_full,
                a.p_0,
              ]}>
              <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                <Logo allowVariants={false} width={36} fill={LIGHT} />
                <View style={{paddingTop: 6}}>
                  <Logotype width={120} fill={LIGHT} />
                </View>
              </View>
            </View>
            <View
              style={[a.gap_sm, a.align_center, a.pt_5xl, a.pb_3xl, a.mt_2xl]}>
              <Text
                style={[
                  gtMobile ? a.text_4xl : a.text_3xl,
                  a.font_semi_bold,
                  a.text_center,
                  {color: LIGHT},
                  web({
                    backgroundImage:
                      'linear-gradient(180deg, #F5F0FF 0%, #C8BFD9 83.65%, rgba(200, 191, 217, 0.50) 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    color: 'transparent',
                    lineHeight: 1.2,
                    letterSpacing: -0.5,
                  }),
                ]}>
                <Trans>Join the cookout.</Trans>
                {'\n'}
                <Trans>Invite only.</Trans>
                {'\n'}
                <Trans>For us by us.</Trans>
              </Text>
            </View>
            <View style={[a.gap_md, a.align_center]}>
              <View>
                <Button
                  onPress={onPressCreateAccount}
                  label={l`Create account`}
                  size="large"
                  color="primary"
                  style={{
                    width: 200,
                    backgroundColor: MAGENTA,
                  }}>
                  <ButtonText>
                    <Trans>Create account</Trans>
                  </ButtonText>
                </Button>
                <Button
                  onPress={onPressExplore}
                  label={l`Explore the app`}
                  size="large"
                  color="primary"
                  variant="ghost"
                  style={[a.bg_transparent, {width: 200}]}
                  hoverStyle={[a.bg_transparent]}>
                  {({hovered}) => (
                    <ButtonText
                      style={[hovered && [a.underline], {color: MAGENTA}]}>
                      <Trans>Explore the app</Trans>
                    </ButtonText>
                  )}
                </Button>
              </View>
              <View style={[a.align_center, {minWidth: 200}]}>
                <Text
                  style={[
                    a.text_md,
                    a.text_center,
                    {color: LIGHT_MUTED, lineHeight: 24},
                  ]}>
                  <Trans>Already have an account?</Trans>{' '}
                  <Pressable
                    onPress={onPressSignIn}
                    onPointerEnter={() => setSignInLinkHovered(true)}
                    onPointerLeave={() => setSignInLinkHovered(false)}
                    accessibilityRole="button"
                    accessibilityLabel={l`Sign in`}
                    accessibilityHint="">
                    <Text
                      style={[
                        a.font_medium,
                        {
                          color: MAGENTA,
                          fontSize: undefined,
                        },
                        signInLinkHovered && a.underline,
                      ]}>
                      <Trans>Sign in</Trans>
                    </Text>
                  </Pressable>
                </Text>
              </View>
            </View>
          </View>
          <Button
            label={l`Close welcome modal`}
            style={[
              a.absolute,
              {
                top: 8,
                right: 8,
                zIndex: 2,
              },
              a.bg_transparent,
            ]}
            hoverStyle={[a.bg_transparent]}
            onPress={() => {
              ax.metric('welcomeModal:dismissed', {})
              fadeOutAndClose()
            }}
            color="secondary"
            size="small"
            variant="ghost"
            shape="round">
            {({hovered, pressed, focused}) => (
              <XIcon
                size="md"
                style={{
                  color: LIGHT,
                  opacity: hovered || pressed || focused ? 1 : 0.7,
                }}
              />
            )}
          </Button>
        </View>
      </FocusScope.FocusScope>
    </View>
  )
}
